/**
 * Admin → Settings → System Logs
 *
 * Persistent application event log surfaced in the UI so admins can
 * troubleshoot production issues (failed uploads, checksum mismatches,
 * email failures, background job errors) without backend access.
 *
 * Backed by `/api/admin/system-logs` (see `routes/system_logs.py`).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    AlertOctagon, AlertTriangle, Info, RefreshCw, Search, X, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_STYLE = {
    ERROR:   { color: 'text-red-700',     bg: 'bg-red-50',    border: 'border-red-200',    Icon: AlertOctagon },
    WARNING: { color: 'text-amber-700',   bg: 'bg-amber-50',  border: 'border-amber-200',  Icon: AlertTriangle },
    INFO:    { color: 'text-slate-600',   bg: 'bg-slate-50',  border: 'border-slate-200',  Icon: Info },
};

function authHeaders() {
    return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

function formatTs(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString();
}

export default function SystemLogsPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [byLevel, setByLevel] = useState({ ERROR: 0, WARNING: 0, INFO: 0 });
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    // Filters
    const [level, setLevel] = useState('');
    const [module, setModule] = useState('');
    const [q, setQ] = useState('');
    const [orderNumber, setOrderNumber] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: '200' });
            if (level) params.set('level', level);
            if (module) params.set('module', module);
            if (q) params.set('q', q);
            if (orderNumber) params.set('order_number', orderNumber);
            if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
            if (dateTo) params.set('date_to', new Date(dateTo).toISOString());
            const res = await fetch(`${API_URL}/api/admin/system-logs?${params}`, { headers: authHeaders() });
            const data = await res.json();
            setItems(data.items || []);
            setTotal(data.total || 0);
            setByLevel(data.by_level || { ERROR: 0, WARNING: 0, INFO: 0 });
        } finally {
            setLoading(false);
        }
    }, [level, module, q, orderNumber, dateFrom, dateTo]);

    useEffect(() => {
        fetch(`${API_URL}/api/admin/system-logs/modules`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : [])
            .then(setModules)
            .catch(() => {});
        // eslint-disable-next-line
    }, []);

    /* eslint-disable */
    useEffect(() => { fetchLogs(); }, [fetchLogs]);
    /* eslint-enable */

    const clearAll = () => {
        // Reset every filter input to empty
        setLevel('');
        setModule('');
        setQ('');
        setOrderNumber('');
        setDateFrom('');
        setDateTo('');
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="system-logs-page">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
                        <FileText className="h-6 w-6 text-indigo-500" />
                        System Logs
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Application errors, warnings and events. Retained for 90 days.
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchLogs}
                    disabled={loading}
                    data-testid="refresh-logs-btn"
                >
                    <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Summary tiles */}
            <div className="grid grid-cols-3 gap-3 mb-6">
                {['ERROR', 'WARNING', 'INFO'].map(lvl => {
                    const s = LEVEL_STYLE[lvl];
                    return (
                        <button
                            key={lvl}
                            onClick={() => setLevel(level === lvl ? '' : lvl)}
                            className={`p-4 rounded-lg border-2 text-left transition-all hover:shadow-md ${level === lvl ? 'ring-2 ring-indigo-300 ' + s.border : s.border} ${s.bg}`}
                            data-testid={`tile-${lvl.toLowerCase()}`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <s.Icon className={`h-4 w-4 ${s.color}`} />
                                <span className={`text-xs font-semibold ${s.color}`}>{lvl}</span>
                            </div>
                            <div className="text-3xl font-bold text-slate-900">{byLevel[lvl] ?? 0}</div>
                        </button>
                    );
                })}
            </div>

            {/* Filters */}
            <Card className="mb-6">
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center justify-between">
                        <span className="flex items-center gap-2"><Search className="h-4 w-4" /> Filters</span>
                        <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs">
                            <X className="h-3 w-3 mr-1" /> Clear all
                        </Button>
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <div className="space-y-1">
                        <Label className="text-xs">Level</Label>
                        <select value={level} onChange={(e) => setLevel(e.target.value)}
                            className="h-9 w-full border rounded-md px-2 text-sm" data-testid="filter-level">
                            <option value="">All</option>
                            <option value="ERROR">ERROR</option>
                            <option value="WARNING">WARNING</option>
                            <option value="INFO">INFO</option>
                        </select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Module</Label>
                        <select value={module} onChange={(e) => setModule(e.target.value)}
                            className="h-9 w-full border rounded-md px-2 text-sm" data-testid="filter-module">
                            <option value="">All</option>
                            {modules.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Order #</Label>
                        <Input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)}
                            placeholder="e.g. 190" className="h-9" data-testid="filter-order" />
                    </div>
                    <div className="space-y-1 col-span-1 md:col-span-2">
                        <Label className="text-xs">Search message</Label>
                        <Input value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="IMAGE.DAT, mismatch..." className="h-9" data-testid="filter-q" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">From</Label>
                        <Input type="datetime-local" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">To</Label>
                        <Input type="datetime-local" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" />
                    </div>
                </CardContent>
            </Card>

            {/* Log list */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm">
                        {total} entries
                        {(level || module || q || orderNumber || dateFrom || dateTo) && (
                            <span className="text-xs text-slate-400 font-normal ml-2">(filtered)</span>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                    {items.length === 0 && !loading && (
                        <div className="py-10 text-center text-sm text-slate-500">
                            No log entries match the current filters.
                        </div>
                    )}
                    {items.map((entry) => {
                        const s = LEVEL_STYLE[entry.level] || LEVEL_STYLE.INFO;
                        const isExpanded = expandedId === entry.id;
                        return (
                            <div
                                key={entry.id}
                                className={`rounded-lg border ${s.border} ${s.bg} transition-all`}
                                data-testid={`log-row-${entry.id}`}
                            >
                                <button
                                    onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                                    className="w-full flex items-start gap-3 p-3 text-left hover:bg-black/[0.02]"
                                >
                                    <s.Icon className={`h-4 w-4 ${s.color} flex-shrink-0 mt-0.5`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${s.color} ${s.bg} border ${s.border}`}>
                                                {entry.level}
                                            </span>
                                            <span className="text-[11px] font-semibold text-slate-700">{entry.module}</span>
                                            {entry.order_number && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                                                    Order #{String(entry.order_number).padStart(6, '0')}
                                                </span>
                                            )}
                                            <span className="text-[10px] text-slate-400 ml-auto">{formatTs(entry.created_at)}</span>
                                        </div>
                                        <p className="text-sm text-slate-800 break-words">{entry.message}</p>
                                    </div>
                                </button>
                                {isExpanded && (
                                    <div className="border-t border-slate-200 p-3 bg-white/60">
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-[11px] text-slate-600 mb-2">
                                            {entry.order_id && <div><span className="text-slate-400">order_id:</span> <span className="font-mono">{entry.order_id}</span></div>}
                                            {entry.file_id && <div><span className="text-slate-400">file_id:</span> <span className="font-mono">{entry.file_id}</span></div>}
                                            {entry.user_name && <div><span className="text-slate-400">user:</span> {entry.user_name}</div>}
                                            {entry.customer_id && <div><span className="text-slate-400">customer_id:</span> <span className="font-mono">{entry.customer_id}</span></div>}
                                            {entry.hostname && <div><span className="text-slate-400">host:</span> {entry.hostname}</div>}
                                        </div>
                                        {entry.details && Object.keys(entry.details).length > 0 && (
                                            <pre className="text-[11px] bg-slate-900 text-slate-100 rounded p-2 overflow-x-auto leading-snug max-h-72">
{JSON.stringify(entry.details, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </CardContent>
            </Card>
        </div>
    );
}
